import React, { useState } from "react";

/**
 * Carry-forward on a repeat visit. Modeled on Csa/CsaCarry.js.
 *
 * Silently inheriting an answer and deliberately comparing against one are
 * different things. Carrying is a convenience - it saves retyping hundreds of
 * checkpoints that mostly have not changed. It is not a finding. Last year's
 * "NO - signage missing" must not become this year's report because nobody
 * looked.
 *
 * So every carried answer arrives unconfirmed, submit stays blocked while any
 * remain, and the auditor has to pass their eye over each one:
 *
 *   CarryOffer     the choice, made once, with the counts stated plainly
 *   CarryBanner    what is still outstanding, and the way to clear it
 *   PreviousLine   last visit's answer, beside this visit's checkpoint
 */

/* ------------------------------------------------------------------ */
/* the offer                                                           */
/* ------------------------------------------------------------------ */

export function CarryOffer({ preview, busy, onCarry, onStartFresh }) {
  const [showDetail, setShowDetail] = useState(false);

  if (!preview || !preview.available) return null;

  const p = preview.prior || {};
  const c = preview.counts || {};

  return (
    <>
      <div className="elec-pad-scrim" />
      <div className="carry-dialog" role="dialog" aria-modal="true" aria-labelledby="carry-h">
        <h3 id="carry-h">This site was audited before</h3>

        <p className="carry-prior">
          <strong>{p.report_id}</strong>
          <span className="carry-when">
            {fmtDate(p.start_date)}
            {p.end_date && p.end_date !== p.start_date ? ` – ${fmtDate(p.end_date)}` : ""}
          </span>
          {p.overall_pct == null ? null : (
            <span className="carry-score">
              {p.overall_pct}% · {p.maturity_band}
            </span>
          )}
        </p>

        <p className="carry-summary">{preview.summary}</p>

        {!preview.same_version ? (
          <p className="carry-warn">
            The checklist has changed since that audit. Checkpoints that were
            reworded are carried, but read them again — the answer that was
            true of the old wording may not be true of the new one.
          </p>
        ) : null}

        <p className="carry-gate">
          Carried answers are a starting point, not findings. Every one is
          marked unconfirmed, and you cannot submit until you have been
          through them.
        </p>

        {c.fresh || c.reworded ? (
          <button
            type="button"
            className="carry-detail-toggle"
            onClick={() => setShowDetail(!showDetail)}
          >
            {showDetail ? "▾ Hide which ones" : "▸ Which checkpoints are new or reworded"}
          </button>
        ) : null}

        {showDetail ? (
          <div className="carry-detail">
            {preview.fresh && preview.fresh.length ? (
              <>
                <h4>New in this version — nobody has answered these here</h4>
                <ul>
                  {preview.fresh.map((q) => (
                    <li key={q.code || q}>
                      <span className="cd-code">{q.code || q}</span>
                      {q.check_points ? <span>{q.check_points}</span> : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {preview.reworded && preview.reworded.length ? (
              <>
                <h4>Reworded since the last audit</h4>
                <ul>
                  {preview.reworded.map((q) => (
                    <li key={q.code || q}>
                      <span className="cd-code">{q.code || q}</span>
                      {q.check_points ? <span>{q.check_points}</span> : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}

        <div className="carry-actions">
          <button type="button" className="small-btn" disabled={busy} onClick={onStartFresh}>
            Start fresh
          </button>
          <button type="button" className="cap-btn" disabled={busy} onClick={onCarry}>
            {busy ? "Carrying…" : `Carry ${c.carried == null ? "" : c.carried + " "}forward`}
          </button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* what is still outstanding                                           */
/* ------------------------------------------------------------------ */

export function CarryBanner({ outstanding, total, busy, onReview, onConfirmAll }) {
  const [arming, setArming] = useState(false);

  if (!outstanding) return null;

  return (
    <div className="carry-banner">
      <div className="cb-text">
        <strong>
          {outstanding} carried answer{outstanding === 1 ? "" : "s"} still to check
        </strong>
        <span className="cb-sub">
          {total ? `of ${total} brought forward from the last visit. ` : ""}
          You cannot submit until these have been through.
        </span>
      </div>

      {arming ? (
        <>
          <span className="cb-arm">
            Confirm all {outstanding} without reading them?
          </span>
          <button type="button" className="small-btn" onClick={() => setArming(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="small-btn warn"
            disabled={busy}
            onClick={() => {
              setArming(false);
              onConfirmAll();
            }}
          >
            Yes, confirm all
          </button>
        </>
      ) : (
        <>
          <button type="button" className="small-btn" onClick={onReview}>
            Go to the first one
          </button>
          <button
            type="button"
            className="small-btn"
            disabled={busy}
            onClick={() => setArming(true)}
          >
            Confirm all
          </button>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* beside the checkpoint                                               */
/* ------------------------------------------------------------------ */

/**
 * "Last time: NO — Permit register unsigned since March."
 *
 * Shown on every checkpoint that has a previous answer, whether or not it was
 * carried. `carried` adds the confirm control: this row was inherited and has
 * not been looked at yet.
 */
export function PreviousLine({ previous, carried, confirmed, disabled, onConfirm }) {
  if (!previous && !carried) return null;

  const changedSince =
    previous && carried && carried.status && carried.status !== previous.status;

  return (
    <div
      className={
        "q-prev" +
        (carried && !confirmed ? " unconfirmed" : "") +
        (confirmed ? " ok" : "")
      }
    >
      {previous ? (
        <p className="qp-last">
          <span className="qp-label">Last time</span>
          <span className={"qp-ans a-" + String(previous.status || "").toLowerCase().replace(/[^a-z]/g, "")}>
            {previous.status || "—"}
          </span>
          {previous.observation ? <span className="qp-note">{previous.observation}</span> : null}
        </p>
      ) : (
        <p className="qp-last">
          <span className="qp-label">Last time</span>
          <span className="qp-none">not answered</span>
        </p>
      )}

      {carried && !confirmed ? (
        <div className="qp-gate">
          <span className="qp-ask">
            {changedSince
              ? "You have changed this — confirm you meant to."
              : "Still true this visit?"}
          </span>
          <button
            type="button"
            className="small-btn"
            disabled={disabled}
            onClick={onConfirm}
          >
            Yes, still true
          </button>
        </div>
      ) : null}

      {carried && confirmed ? <span className="qp-done">Checked this visit</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function fmtDate(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return s;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(day)} ${months[Number(m) - 1] || m} ${y}`;
}
